import { Skeleton } from '@src/components/ui/skeleton';
import { cn } from '@src/lib/utils';

import { base64ToBlob } from '@shared/util/base64ToBlob';

import { PdfAiAnalysisAction } from './PdfAiAnalysisAction';
import PdfViewer from './PdfViewer';
import { ShadcnUploadFileVM } from './ShadcnUpload';

import { css } from '@emotion/css';
import {
    ShadcnButton,
    ShadcnDrawer,
    ShadcnDrawerProps,
    ShadcnModal,
    useWindowDimensions,
} from '@shadcn-features/core';
import { ShadcnTabulator } from '@shadcn-features/core';
import { Eye } from 'lucide-react';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { read, utils } from 'xlsx';

export type FileContent = {
    content: string;
    extension: string;
    name: string;
};

const imgTypes: string[] = [
    'image/gif',
    'image/jpeg',
    'image/png',
    'image/webp',
];
const pdfTypes: string[] = ['application/pdf'];
const docxTypes: string[] = [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const xlsxTypes: string[] = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

const isPdfFile = (fileType: string | undefined, fileName: string) => {
    const normalizedType = fileType?.toLowerCase().split(';')[0];

    return (
        normalizedType === 'application/pdf' ||
        normalizedType === 'pdf' ||
        fileName.toLowerCase().endsWith('.pdf')
    );
};

function FilePreviewContent(file: FileContent) {
    const fileType = file.extension;

    if (imgTypes.includes(fileType)) {
        return <ImagePreview {...file} />;
    }

    if (pdfTypes.includes(fileType)) {
        return <PdfViewer pdf={file.content} />;
    }

    if (docxTypes.includes(fileType)) {
        return <DocxPreview {...file} />;
    }

    if (xlsxTypes.includes(fileType)) {
        return <XlsxPreview {...file} />;
    }

    return null;
}

const isPreview = (fileType?: string) => {
    return (
        fileType &&
        (imgTypes.includes(fileType) ||
            pdfTypes.includes(fileType) ||
            docxTypes.includes(fileType) ||
            xlsxTypes.includes(fileType))
    );
};

export const FilePreview = <T extends ShadcnUploadFileVM>({
    onDownload,
    file,
    onPreview,
    drawerProps,
    previewText,
    wrapperType = 'drawer',
    triggerProps,
    enablePdfAiAnalysis = false,
    renderTriggerBtn,
}: {
    onDownload?: (
        params: T,
    ) => FileContent | undefined | Promise<FileContent | undefined>;
    file: T;
    onPreview?: (params: T) => unknown;
    drawerProps?: ShadcnDrawerProps;
    previewText?: string | ReactNode;
    triggerProps?: React.ComponentPropsWithoutRef<typeof ShadcnButton>;
    wrapperType?: 'drawer' | 'modal';
    enablePdfAiAnalysis?: boolean;
    renderTriggerBtn?: (cb: () => void) => ReactNode;
}) => {
    const [previewFile, setPreviewFile] = useState<FileContent | undefined>();
    const { t } = useTranslation();
    const { isMobile } = useWindowDimensions();

    const extension =
        'extension' in file
            ? (file.extension as string)
            : 'fileType' in file
              ? (file.fileType as string)
              : undefined;
    const showPdfAiAnalysis =
        enablePdfAiAnalysis &&
        isPdfFile(extension, file.name) &&
        !!previewFile?.content;
    const pdfAiAnalysisAction = showPdfAiAnalysis ? (
        <PdfAiAnalysisAction loadFile={() => previewFile} />
    ) : null;
    const previewTitle = drawerProps?.title ?? t('preview');
    const useDrawer = wrapperType === 'drawer' || isMobile;

    const wrapper = useDrawer ? (
        <ShadcnDrawer
            destroyOnClose
            container={false}
            placement='left'
            width={'100%'}
            {...drawerProps}
            open={!!previewFile}
            title={previewTitle}
            extra={
                pdfAiAnalysisAction || drawerProps?.extra ? (
                    <div className='flex items-center gap-2'>
                        {drawerProps?.extra}
                        {pdfAiAnalysisAction}
                    </div>
                ) : undefined
            }
            onClose={() => setPreviewFile(undefined)}
        >
            {previewFile && (
                <FilePreviewContent
                    {...previewFile}
                    extension={extension || previewFile.extension}
                />
            )}
        </ShadcnDrawer>
    ) : (
        <ShadcnModal
            destroyOnClose
            container={false}
            modal={true}
            width='calc(100vw - 32px)'
            {...drawerProps}
            open={!!previewFile}
            styles={{
                ...drawerProps?.styles,
                body: {
                    display: 'flex',
                    flex: 1,
                    minHeight: 0,
                    padding: 16,
                    ...drawerProps?.styles?.body,
                },
                content: {
                    height: 'calc(100dvh - 32px)',
                    maxHeight: 'calc(100dvh - 32px)',
                    ...drawerProps?.styles?.content,
                },
            }}
            title={
                pdfAiAnalysisAction ? (
                    <div className='flex w-full items-center justify-between gap-4'>
                        <span className='font-semibold'>{previewTitle}</span>
                        {pdfAiAnalysisAction}
                    </div>
                ) : (
                    previewTitle
                )
            }
            onClose={() => setPreviewFile(undefined)}
        >
            {previewFile && (
                <FilePreviewContent
                    {...previewFile}
                    extension={extension || previewFile.extension}
                />
            )}
        </ShadcnModal>
    );
    return (
        <>
            {wrapper}
            {((isPreview(extension) && onDownload) || onPreview) && (
                <>
                    {!renderTriggerBtn && (
                        <ShadcnButton
                            size={'sm'}
                            variant={'outline'}
                            className='hidden md:flex'
                            icon={
                                <Eye
                                    className={css`
                                        height: 1rem;
                                        width: 1rem;
                                    `}
                                />
                            }
                            onClick={async () => {
                                if (onPreview) {
                                    await onPreview(file);
                                } else {
                                    const pFile = await onDownload?.(file);
                                    setPreviewFile(pFile);
                                }
                            }}
                            {...triggerProps}
                        >
                            {previewText}
                        </ShadcnButton>
                    )}
                    {renderTriggerBtn &&
                        renderTriggerBtn(async () => {
                            if (onPreview) {
                                await onPreview(file);
                            } else {
                                const pFile = await onDownload?.(file);
                                setPreviewFile(pFile);
                            }
                        })}
                </>
            )}
        </>
    );
};

function XlsxPreview(file: FileContent) {
    const [json, setJson] = useState<Record<string, unknown>[]>([]);

    useEffect(() => {
        const makeJson = async () => {
            const workbook = read(file.content);
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            const res = utils.sheet_to_json<Record<string, unknown>>(worksheet);
            setJson(res);
        };

        void makeJson();
    }, [file]);

    const columns = useMemo(() => {
        const headings = json[0];
        if (!headings) {
            return [];
        }

        return Object.keys(headings).map((key) => ({
            dataIndex: key,
            title: key === '__EMPTY' ? '' : key,
        }));
    }, [json]);

    return (
        <ShadcnTabulator
            borderRadius
            transparent
            columns={columns}
            dataSource={json}
            initialParams={{ size: 15 }}
            title={file.name}
            rowKey={(row: Record<string, unknown>) =>
                Object.values(row).join('|')
            }
        />
    );
}

function DocxPreview(file: FileContent) {
    const containerRef = useRef<HTMLDivElement | null>(null);

    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const render = async () => {
            setLoading(true);
            const blob = base64ToBlob(
                file.content,
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            );

            if (blob && containerRef.current) {
                const { renderAsync } = await import('docx-preview');
                await renderAsync(
                    blob,
                    containerRef.current,
                    containerRef.current,
                );
            }

            setLoading(false);
        };

        void render();
    }, [file]);

    return (
        <>
            {loading && (
                <Skeleton
                    className={css`
                        width: 100%;
                        height: 100%;
                    `}
                />
            )}
            <div
                className={cn('w-full overflow-scroll')}
                ref={containerRef}
            ></div>
        </>
    );
}

function ImagePreview(file: FileContent) {
    if (!file.content) {
        return null;
    }

    const blob = base64ToBlob(file.content, file.extension);
    const url = URL.createObjectURL(blob);

    return (
        <div
            className={css`
                display: flex;
                align-items: center;
                justify-content: center;
                margin: 0 auto;
            `}
        >
            <img
                alt={file.name}
                src={url}
                className={css`
                    min-width: 300px;
                    max-width: 100%;
                    max-height: 100%;
                    object-fit: contain;
                `}
            />
        </div>
    );
}
